import { Link, NavLink, useNavigate } from "react-router-dom";
import { Phone, Mail, MessageCircle, Menu, LogOut, LayoutDashboard, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "./Logo";
import { CONTACT, initials } from "@/lib/hz";
import { useMe, useLogout } from "@/hooks/useMe";

const linksFor = (me) => {
  if (me?.role === "cliente") return [["/panel", "Mi panel"], ["/agenda", "Agenda"], ["/crear-solicitud", "Nueva solicitud"], ["/profesionales", "Profesionales"], ["/facturas", "Facturas y pagos"], ["/soporte", "Atención al cliente"]];
  if (me?.role === "profesional") return [["/panel", "Mi panel"], ["/agenda", "Agenda"], ["/formacion", "Formación"], ["/facturas", "Facturas y cobros"], ["/profesionales", "Profesionales"], ["/soporte", "Atención al cliente"]];
  return [["/profesionales", "Profesionales"], ["/crear-solicitud", "Pedir presupuesto"], ["/soporte", "Atención al cliente"], ["/descargar-app", "La app"]];
};

const TopBar = () => (
  <div className="hidden bg-slate-950 text-xs text-slate-300 md:block">
    <div className="mx-auto flex h-8 max-w-6xl items-center justify-between px-4">
      <span>Atención al cliente {CONTACT.hours}</span>
      <div className="flex items-center gap-5">
        <a href={`tel:${CONTACT.phone}`} data-testid="topbar-phone" className="flex items-center gap-1.5 hover:text-white"><Phone className="size-3.5" />{CONTACT.phoneDisplay}</a>
        <a href={CONTACT.whatsappUrl} target="_blank" rel="noreferrer" data-testid="topbar-whatsapp" className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300"><MessageCircle className="size-3.5" />WhatsApp</a>
        <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-1.5 hover:text-white"><Mail className="size-3.5" />{CONTACT.email}</a>
      </div>
    </div>
  </div>
);

const UserMenu = ({ me }) => {
  const logout = useLogout();
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-slate-200 py-1 pl-1 pr-3 text-sm font-semibold hover:border-orange-300">
          <span className="grid size-7 place-items-center rounded-full bg-slate-900 text-[11px] text-white">{initials(me.name)}</span>
          <span className="hidden sm:inline">{me.name.split(" ")[0]}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{me.name}<div className="text-xs font-normal capitalize text-slate-500">{me.role}</div></DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-panel" onClick={() => nav("/panel")}><LayoutDashboard className="mr-2 size-4" />Mi panel</DropdownMenuItem>
        {me.role === "profesional" && <DropdownMenuItem data-testid="menu-public-profile" onClick={() => nav(`/profesionales/${me.id}`)}><User className="mr-2 size-4" />Mi perfil público</DropdownMenuItem>}
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}><LogOut className="mr-2 size-4" />Cerrar sesión</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const navCls = ({ isActive }) => `rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${isActive ? "bg-orange-50 text-orange-600" : "text-slate-600 hover:text-slate-900"}`;

export const Header = () => {
  const { data: me } = useMe();
  const links = linksFor(me);
  return (
    <header className="sticky top-0 z-40">
      <TopBar />
      <div className="border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-4">
          <Logo />
          <nav className="hidden items-center gap-1 lg:flex">
            {links.map(([to, label]) => <NavLink key={to} to={to} className={navCls} data-testid={`nav-${to.slice(1)}`}>{label}</NavLink>)}
          </nav>
          <div className="flex items-center gap-2">
            {me ? <UserMenu me={me} /> : (
              <>
                <Link to="/login" data-testid="header-login-btn" className="hidden px-3 text-sm font-semibold text-slate-700 hover:text-slate-900 sm:block">Entrar</Link>
                <Button asChild size="sm" className="rounded-lg"><Link to="/register" data-testid="header-register-btn">Crear cuenta</Link></Button>
              </>
            )}
            <Sheet>
              <SheetTrigger asChild><Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn"><Menu /></Button></SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetTitle className="font-heading">Menú</SheetTitle>
                <nav className="mt-6 flex flex-col gap-1">
                  {links.map(([to, label]) => <NavLink key={to} to={to} className={navCls}>{label}</NavLink>)}
                  {!me && <NavLink to="/login" className={navCls}>Entrar</NavLink>}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
